'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { RenderOneBlock, ThemeContext } from '@akademi/site-builder-runtime';
import { palette } from '../styles/palette';
import type { SkinWidth } from './skinnedBlocks';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Styles the platform's functional blocks (basket, checkout, account, search,
 * forms) to this design system WITHOUT forking them.
 *
 * <h3>How</h3>
 * The theme registers a thin override under the generic block's own type. The
 * override renders a wrapper and then renders the SAME block type again under
 * the `default` theme id, so the resolver falls through to the platform's
 * canonical component. All behaviour (basket state, checkout, sign-in, order
 * look-ups) stays in commerce.
 *
 * <p>The wrapper restyles in three layers, least invasive first:
 * <ol>
 *   <li><b>Tokens.</b> Most of the look (colours, radii, fonts) already comes
 *       from the template's tokens, which the generic blocks read.</li>
 *   <li><b>Scoped CSS variables.</b> A few variables the generic blocks read
 *       that are not token paths (`--color-primary`, `--color-on-primary`,
 *       `--color-border`, `--color-text`) are set on the wrapper, so the blocks'
 *       own inline `var(...)` lookups resolve to the kitchen palette.</li>
 *   <li><b>Scoped rules.</b> Headings take the serif, buttons take the brief's
 *       5 px / 10 px 24 px / 0.875 rem shape, fields get an AA-contrast border
 *       and a visible focus ring. Where a generic block sets the same property
 *       inline, the rule needs `!important`; those are the only ones that use it.</li>
 * </ol>
 */
const SKIN_CSS = `
.sk-skin{
  --color-primary:var(--sk-sage);
  --color-on-primary:#fff;
  --color-text:var(--sk-ink);
  --color-border:var(--sk-control-border);
  --color-course:var(--sk-sage-deep);
  padding-block:clamp(40px,4vw + 1rem,96px);
}
.sk-skin--narrow > .sk-skin__inner{max-width:40rem}
.sk-skin--medium > .sk-skin__inner{max-width:64rem}
.sk-skin__inner{margin-inline:auto;padding-inline:var(--sk-gutter);max-width:var(--sk-max)}
.sk-skin h1,.sk-skin h2{font-family:var(--sk-serif);font-weight:300;letter-spacing:-.01em;line-height:1.2;color:var(--sk-ink)}
.sk-skin h1{font-size:clamp(2rem,1.5rem + 1.8vw,3rem) !important}
.sk-skin h3,.sk-skin h4{font-family:var(--sk-serif);font-weight:400;color:var(--sk-ink)}
.sk-skin label{color:var(--sk-ink)}
.sk-skin button{font-family:var(--sk-sans)}
.sk-skin button[type="submit"],.sk-skin form button:not([type="button"]){padding:10px 24px !important;border-radius:var(--sk-radius-button) !important;font-size:.875rem !important;font-weight:500 !important}
.sk-skin input:not([type="checkbox"]):not([type="radio"]),.sk-skin select,.sk-skin textarea{border-color:var(--sk-control-border) !important;border-radius:var(--sk-radius-button) !important;color:var(--sk-ink);background-color:#fff}
.sk-skin input:focus-visible,.sk-skin select:focus-visible,.sk-skin textarea:focus-visible,.sk-skin button:focus-visible,.sk-skin a:focus-visible{outline:2px solid var(--sk-focus) !important;outline-offset:2px}
.sk-skin input[type="checkbox"],.sk-skin input[type="radio"]{accent-color:${palette.sageDeep}}
.sk-skin a{color:var(--sk-sage-deep);text-underline-offset:.25em}
.sk-skin a:hover{color:var(--sk-ink)}
`;

const DelegatingType = createContext<string | null>(null);

function SkinFrame({ type, width, children }: { type: string; width: SkinWidth; children: ReactNode }) {
  return (
    <div className={`sk sk-skin sk-skin--${width}`} data-sk-skin={type}>
      <SkBaseStyles />
      <SkStyles id="functional-skin" css={SKIN_CSS} />
      <div className="sk-skin__inner">{children}</div>
    </div>
  );
}

/** The props the skin needs on top of the block's own, namespaced so they cannot collide. */
export interface KitchenSkinProps {
  skinType: string;
  skinWidth: SkinWidth;
  blockProps: Record<string, unknown>;
}

/**
 * The skin itself: one component, told at render time which block it is
 * wrapping and how wide to sit.
 *
 * <p><strong>This used to be a factory — `withKitchenSkin(type, width)` —
 * called once per block while building the theme bundle, and that is what made
 * the storefront unbuildable.</strong> The bundle is assembled at module scope
 * in `../index.ts`, which the server reaches through the extensions module
 * (`app/api/storefront/extensions/[...path]/route.ts` imports it for its API
 * prefixes). A function exported from a `'use client'` module is only a
 * reference on the server, so calling it there threw
 * {@code Attempted to call kitchenSkinOverrides() from the server} and the
 * whole build failed at page-data collection — silently, for a long while,
 * because a cached Docker layer kept rebuilding an older module graph with no
 * extensions in it at all.
 *
 * <p>Taking the two values as props moves the only call back inside the client
 * boundary: `./kitchenSkinOverrides.tsx` builds plain wrappers that *render*
 * this component rather than *calling* anything across the boundary, which is
 * the one direction React allows.
 */
export function KitchenSkin({ skinType, skinWidth, blockProps }: KitchenSkinProps) {
  const delegating = useContext(DelegatingType);
  // Re-entered for the same type: the default theme resolved back to this
  // override, so there is no canonical block to delegate to. Render nothing
  // rather than recurse.
  if (delegating === skinType) return null;
  return (
    <SkinFrame type={skinType} width={skinWidth}>
      <DelegatingType.Provider value={skinType}>
        <ThemeContext.Provider value={null}>
          <RenderOneBlock instance={{ type: skinType, props: blockProps }} />
        </ThemeContext.Provider>
      </DelegatingType.Provider>
    </SkinFrame>
  );
}
